import type { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Directive, EventEmitter, Host, Input, NgZone, Optional, Output } from '@angular/core';
import { NgControl } from '@angular/forms';

import { deepEqual } from 'fast-equals';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[rtFormError]',
  standalone: true,
})
export class FormErrorDirective implements OnChanges, OnInit, OnDestroy {
  @Input('rtForceValidate')
  public markAsDirty: boolean;

  @Input('rtFormError') public errors: { [key: string]: string };

  @Output() public error = new EventEmitter<{
    control: NgControl;
    errors: { [key: string]: string } | [];
    isForced: boolean;
  }>();

  private ngOnDestroy$ = new Subject<void>();
  private prevErrorMessages: { [key: string]: string } | [] = {};
  private isForced: boolean = false;
  private statusChanged = false;

  constructor(
    @Optional()
    @Host()
    public ngControl: NgControl,
    private zone: NgZone,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    const { markAsDirty } = changes;
    if (markAsDirty) {
      this.forceValidate(this.markAsDirty);
    }

    if (changes.errors && this.errors && this.statusChanged) {
      this.onStatusChange();
    }
  }

  public ngOnInit(): void {
    this.ngControl
      .statusChanges!.pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => this.onStatusChange());
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private onStatusChange(): void {
    if (this.ngControl.errors && this.errors) {
      const errorMessages = Object.keys(this.ngControl.errors).reduce(
        (acc, key) => ({ ...acc, [key]: this.errors[key] }),
        {},
      );
      if (this.markAsDirty || !deepEqual(this.prevErrorMessages, errorMessages)) {
        this.emitErrors(errorMessages);
      }
    } else if (this.prevErrorMessages && Object.keys(this.prevErrorMessages).length) {
      this.emitErrors([]);
    }

    this.statusChanged = true;
    this.isForced = false;
  }

  private emitErrors(errorMessages: { [key: string]: string } | []): void {
    this.error.next({
      control: this.ngControl,
      errors: errorMessages,
      isForced: this.isForced,
    });

    this.prevErrorMessages = errorMessages;
  }

  private forceValidate(markAsTouched: boolean): void {
    this.zone.onMicrotaskEmpty
      .asObservable()
      .pipe(take(1))
      .subscribe(() => {
        if (markAsTouched) {
          this.ngControl.control!.markAsTouched({ onlySelf: true });
        }

        this.isForced = true;
        this.ngControl.control!.updateValueAndValidity({ onlySelf: true, emitEvent: true });
      });
  }
}
